import { filter } from 'lodash';
import { format, parseISO } from 'date-fns';

export function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

export function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

export function applySortFilter(array = [{}], comparator, query) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  if (query) {
    return filter(array, (_item) => _item.name.toLowerCase().indexOf(query.toLowerCase()) !== -1);
  }
  return stabilizedThis.map((el) => el[0]);
}

export function formatStorageSize(sizeInBytes) {
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let size = sizeInBytes;
  let unitIndex = 0;

  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex += 1;
  }

  return `${size.toFixed(1)} ${units[unitIndex]}`;
}

export function parseStorageSize(sizeString) {
  const match = sizeString.match(/^(\d+(?:\.\d+)?)\s*(B|KB|MB|GB|TB)$/i);
  if (!match) return 0;

  const value = parseFloat(match[1]);
  const unit = match[2].toUpperCase();

  const multipliers = {
    B: 1,
    KB: 1024,
    MB: 1024 * 1024,
    GB: 1024 * 1024 * 1024,
    TB: 1024 * 1024 * 1024 * 1024,
  };

  return value * (multipliers[unit] || 1);
}

export function formatDate(dateString) {
  try {
    return format(parseISO(dateString), 'MM-dd-yy');
  } catch (error) {
    return dateString;
  }
}

export function generateBucketPath(bucketName) {
  const sanitizedName = bucketName
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .trim();

  return `/storage/${sanitizedName}`;
}

export function validateBucketName(name) {
  if (!name || name.trim().length === 0) {
    return 'Bucket name is required';
  }

  if (name.length > 100) {
    return 'Bucket name must be less than 100 characters';
  }

  if (!/^[a-zA-Z0-9\s-_]+$/.test(name)) {
    return 'Bucket name can only contain letters, numbers, spaces, hyphens, and underscores';
  }

  return null;
}

export function validateBucketPath(path) {
  if (!path || path.trim().length === 0) {
    return 'Path is required';
  }

  if (path.length > 200) {
    return 'Path must be less than 200 characters';
  }

  if (!path.startsWith('/')) {
    return 'Path must start with a forward slash (/)';
  }

  return null;
}

export function calculateStorageUsage(buckets) {
  return buckets.reduce((total, bucket) => {
    const sizeInGB = parseStorageSize(bucket.size) / (1024 * 1024 * 1024);
    return total + sizeInGB;
  }, 0);
}

export function getAvailableUsers(users) {
  return users.filter((user) => !user.assignedBucket);
}

export function getBucketById(buckets, bucketId) {
  return buckets.find((bucket) => bucket.id === bucketId);
}

export function getUserById(users, userId) {
  return users.find((user) => user.id === userId);
}

export function getUserByEmail(users, email) {
  return users.find((user) => user.email === email);
}

export function sortBucketsBySize(buckets, order = 'desc') {
  return [...buckets].sort((a, b) => {
    const sizeA = parseStorageSize(a.size);
    const sizeB = parseStorageSize(b.size);

    if (order === 'desc') {
      return sizeB - sizeA;
    }
    return sizeA - sizeB;
  });
}

export function filterBucketsByUser(buckets, userName) {
  return buckets.filter(
    (bucket) => bucket.assignedUser && bucket.assignedUser.toLowerCase().includes(userName.toLowerCase())
  );
}

export function getStorageStats(buckets) {
  const totalBuckets = buckets.length;
  const assignedBuckets = buckets.filter((b) => b.assignedUser).length;
  const unassignedBuckets = totalBuckets - assignedBuckets;
  const totalStorage = calculateStorageUsage(buckets);
  const averageStoragePerBucket = totalBuckets > 0 ? totalStorage / totalBuckets : 0;

  return {
    totalBuckets,
    assignedBuckets,
    unassignedBuckets,
    totalStorage: formatStorageSize(totalStorage * 1024 * 1024 * 1024),
    averageStoragePerBucket: formatStorageSize(averageStoragePerBucket * 1024 * 1024 * 1024),
  };
}
